import {Body, Container, Content, TrialBanner, TrialBannerStrong, TrialBannerText} from "./styled";
import DropDeskIcon from "@dropDesk/storage/icons";
import {ICONS_DROPDESK} from "@dropDesk/storage/icons/icons.d";
import {useInjection} from "inversify-react";
import {AppController} from "@dropDesk/presentation/app/app.controller";
import {formatCurrency} from "@dropDesk/utils/helpers/common";
import {formatDateToDDMMYYYY} from "@dropDesk/utils/helpers/date_helper";
import {RoutesSubscription} from "@dropDesk/presentation/pages/subscription/controller/subscription.controller";
import {CompanyPaymentInfo} from "@dropDesk/domain/entities/company/company.entity";
import React, {useEffect} from "react";
import ResumeValue from "@dropDesk/presentation/pages/subscription/ui/checkout/resume_value";
import InputsPaymentInfo from "@dropDesk/presentation/pages/subscription/ui/checkout/payment_info";
import PaymentType from "@dropDesk/presentation/pages/subscription/ui/checkout/payment_type";
import {
    CardEntity,
    TokenizeCardEntity,
} from "@dropDesk/domain/entities/payment_method/payment_method.entity";
import {TabsPayment} from "@dropDesk/domain/entities/payment_method/payment_method.enum";
import {PaymentSubscriptionEntity} from "@dropDesk/domain/entities/payment_subscriptions/payment_subscription_entity";
import {observer} from "mobx-react";
import {
    PaymentSubscriptionSimulateEntity
} from "@dropDesk/domain/entities/payment_subscriptions/payment_subscription_simulate.entity";
import {PlanExtensionCatalogEntity} from "@dropDesk/domain/entities/plan_extensions/plan_extension_catalog.entity";
import ExtensionBillingSummary
    from "@dropDesk/presentation/pages/subscription/ui/confirm_extension/extension_billing_summary";
import {
    PlanExtensionSimulateHireEntity
} from "@dropDesk/domain/entities/plan_extensions/plan_extension_simulate_hire.entity";
import {CompanyExtensionEntity} from "@dropDesk/domain/entities/plan_extensions/company_extension.entity";


const Checkout = observer((
    {
        setActiveRoute,
        paymentInfo,
        newPaymentInfo,
        initializePaymentInfo,
        onUpdate,
        loading,
        onSearchCNPJ,
        onSearchZipCode,
        onClickSavePaymentInfo,
        valuePlanOnDemand,
        isEditPaymentInfo,
        handleEditPaymentInfo,
        newTokenizeCard,
        onUpdateTokenizeCard,
        pay,
        setActiveTabPayment,
        cardSaved,
        onClickPayCreditCardSaved,
        newSubscription,
        totalFirstPayment,
        activeTabPayment,
        makePaymentMethod,
        resetPaymentMethod,
        labelFirstPayment,
        simulate,
        oldSubs,
        isLegacy,
        selectedExtension,
        simulateHireResult,
        fromExtension,
        currentPayment,
        installedExtensions
    }: {
        setActiveRoute: (route: RoutesSubscription) => void
        paymentInfo?: CompanyPaymentInfo
        initializePaymentInfo: () => void
        newPaymentInfo: CompanyPaymentInfo
        onUpdate: (paymentInfo: CompanyPaymentInfo) => void
        loading: boolean
        onSearchCNPJ: (document: string) => void,
        onSearchZipCode: (zipCode: string) => void,
        onClickSavePaymentInfo: () => void
        valuePlanOnDemand?: number;
        isEditPaymentInfo: { visible: boolean, canReturnPaymentType: boolean }
        handleEditPaymentInfo: (value?: boolean) => void
        newTokenizeCard: TokenizeCardEntity | null
        onUpdateTokenizeCard: (newTokenizeCard: TokenizeCardEntity) => void
        pay: () => void
        setActiveTabPayment: (value: TabsPayment) => void
        cardSaved?: CardEntity
        onClickPayCreditCardSaved: () => void
        newSubscription?: PaymentSubscriptionEntity
        oldSubs: PaymentSubscriptionEntity
        totalFirstPayment: number
        activeTabPayment: TabsPayment | null,
        makePaymentMethod: () => void
        resetPaymentMethod: () => void
        labelFirstPayment: string
        simulate?: PaymentSubscriptionSimulateEntity
        isLegacy: boolean,
        selectedExtension?: PlanExtensionCatalogEntity | null
        simulateHireResult?: PlanExtensionSimulateHireEntity | null
        fromExtension: boolean
        currentPayment?: TabsPayment,
        installedExtensions: CompanyExtensionEntity[]
    }
) => {

    const appController = useInjection(AppController);
    const colors = appController.theme.colorScheme;

    const showSelectPaymentMethod = !!paymentInfo && !isEditPaymentInfo.visible;
    const showSelectPaymentInfo = !paymentInfo || isEditPaymentInfo.visible;

    const hasTrial = fromExtension && (simulateHireResult?.trialDaysApplied ?? 0) > 0;

    useEffect(() => {
        initializePaymentInfo();
    }, []);

    return (
        <Container>
            <Content>
                <Body fromExtension={fromExtension}>
                    {showSelectPaymentMethod &&
                        <PaymentType
                            setActiveRoute={setActiveRoute}
                            paymentInfo={paymentInfo}
                            handleEditPaymentInfo={handleEditPaymentInfo}
                            newTokenizeCard={newTokenizeCard}
                            onUpdateTokenizeCard={onUpdateTokenizeCard}
                            loading={loading}
                            pay={pay}
                            setActiveTabPayment={setActiveTabPayment}
                            cardSaved={cardSaved}
                            onClickPayCreditCardSaved={onClickPayCreditCardSaved}
                            activeTabPayment={activeTabPayment}
                            makePaymentMethod={makePaymentMethod}
                            resetPaymentMethod={resetPaymentMethod}
                            fromExtension={fromExtension}
                            currentPayment={currentPayment}
                            isTrial={hasTrial}
                        />}
                    {showSelectPaymentInfo &&
                        <InputsPaymentInfo
                            newPaymentInfo={newPaymentInfo}
                            loading={loading}
                            onUpdate={onUpdate}
                            onSearchCNPJ={onSearchCNPJ}
                            onSearchZipCode={onSearchZipCode}
                            onClickSavePaymentInfo={onClickSavePaymentInfo}
                            setActiveRoute={setActiveRoute}
                            canReturnPaymentType={isEditPaymentInfo.canReturnPaymentType}
                            handleEditPaymentInfo={handleEditPaymentInfo}
                            fromExtension={fromExtension}
                        />
                    }
                </Body>
                {newSubscription && !fromExtension && <ResumeValue
                    valuePlanOnDemand={valuePlanOnDemand}
                    newSubscription={newSubscription}
                    totalFirstPayment={totalFirstPayment}
                    labelFirstPayment={labelFirstPayment}
                    showSelectPaymentMethod={showSelectPaymentMethod}
                    simulate={simulate}
                    oldSubs={oldSubs}
                    isLegacy={isLegacy}
                    installedExtensions={installedExtensions}
                />}
                {fromExtension && <ExtensionBillingSummary
                    loading={false}
                    extension={selectedExtension!}
                    simulate={simulateHireResult!}
                    style={{marginLeft: 8, marginTop: showSelectPaymentInfo ? 0 : 38}}
                />}
            </Content>
        </Container>
    );
});
export default Checkout;
